// Pinia store for open overlays (modals and flyouts).
import '@types'
import { acceptHMRUpdate, defineStore, storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import zIndexes from '@/assets/styles/zIndexes.module.scss'
import useAnalytics from '@shared/composables/analytics.js'
import { useCaseStore } from '@/stores/case.js'

/**
 * @typedef {{
 *   name: string,
 *   context: Record<string, unknown>,
 *   zIndex: number
 * }} Overlay
*/

/** @typedef {Ref<{ [name: string]: Overlay }>} OverlaySet */

export const useOverlaysStore = defineStore('overlays', () => {
  /** @type {OverlaySet} */
  const modals = ref({})
  /** @type {OverlaySet} */
  const flyouts = ref({})
  /** @type {OverlaySet} */
  /**
 * @typedef {Overlay & {
 *   minimized: boolean,
  * }} Drawer
  */
  /** @typedef {Ref<{ [name: string]: Drawer }>} DrawerSet */
  const drawers = ref({})
  const drawerIsDragging = ref(false)
  // Scroll positions saved when navigating while an overlay is open.
  const savedScrollPositions = ref([])
  // Navigation stack for flyouts - allows going back to previous flyouts with context
  /** @type {Ref<Overlay[]>} */
  const flyoutNavigationStack = ref([])
  // Flag to indicate we're navigating back (prevents cleanup from interfering)
  const isNavigatingBack = ref(false)
  // So browse can ignore spurious toggle when filters flyout close click retargets
  const lastClosedFlyoutName = ref(null)
  const lastClosedFlyoutAt = ref(0)
  const { trackOverlayInteraction } = useAnalytics()

  const modalName = computed(() => latestModal.value?.name ?? null)
  const modalContext = computed(() => latestModal.value?.context ?? null)
  const modalZIndex = computed(() => latestModal.value?.zIndex ?? null)

  const flyoutName = computed(() => latestFlyout.value?.name ?? null)
  const flyoutContext = computed(() => latestFlyout.value?.context ?? null)
  const flyoutZIndex = computed(() => latestFlyout.value?.zIndex ?? null)
  const hasPreviousFlyout = computed(() => flyoutNavigationStack.value.length > 0)
  const previousFlyout = computed(() => flyoutNavigationStack.value[flyoutNavigationStack.value.length - 1] ?? null)

  const drawerName = computed(() => latestDrawer.value?.name ?? null)
  const drawerContext = computed(() => latestDrawer.value?.context ?? null)
  const drawerZIndex = computed(() => latestDrawer.value?.zIndex ?? null)
  const drawerMinimized = computed(() => latestDrawer.value?.minimized ?? false)
  const isAnyModalOpen = computed(() => Object.keys(modals.value).length > 0)
  const isAnyFlyoutOpen = computed(() => Object.keys(flyouts.value).length > 0)
  const isAnyDrawerOpen = computed(() => {
    const openDrawers = Object.values(drawers.value).filter(drawer => !drawer.minimized)
    return openDrawers.length > 0
  })
  const isAnyDrawerPresent = computed(() => {
    return Object.keys(drawers.value).length > 0
  })
  const isAnyOverlayOpen = computed(() => isAnyModalOpen.value || isAnyFlyoutOpen.value || isAnyDrawerOpen.value)

  const latestModal = computed(() => {
    // Return the modal with the highest z-index
    return Object.values(modals.value).reduce((acc, modal) => {
      return acc && acc.zIndex > modal.zIndex ? acc : modal
    }, null)
  })
  const latestFlyout = computed(() => {
    // Return the flyout with the highest z-index
    return Object.values(flyouts.value).reduce((acc, flyout) => {
      return acc && acc.zIndex > flyout.zIndex ? acc : flyout
    }, null)
  })
  const latestDrawer = computed(() => {
    // Return the drawer with the highest z-index
    return Object.values(drawers.value).reduce((acc, drawer) => {
      return acc && acc.zIndex > drawer.zIndex ? acc : drawer
    }, null)
  })
  const latest = computed(() => {
    const all = [latestModal.value, latestFlyout.value, latestDrawer.value]
      .filter(Boolean)
      .sort((a, b) => b.zIndex - a.zIndex)
  
    if (!all.length) return null
  
    const top = all[0]
    if (top === latestModal.value) return { type: 'modal', ...top }
    if (top === latestFlyout.value) return { type: 'flyout', ...top }
    if (top === latestDrawer.value) return { type: 'drawer', ...top }
    return null
  })
  
  const zIndexAboveIntercom = parseInt(zIndexes['above-intercom'])
  const baseModalZIndex = parseInt(zIndexes.modal)
  const baseFlyoutZIndex = zIndexAboveIntercom
  const baseDrawerZIndex = parseInt(zIndexes.drawer)
  const latestZIndex = computed(() => latest.value?.zIndex ?? null)
  const nextModalZIndex = computed(() => latestZIndex.value >= baseModalZIndex ? latestZIndex.value + 1 : baseModalZIndex)
  const nextFlyoutZIndex = computed(() => latestZIndex.value >= baseFlyoutZIndex ? latestZIndex.value + 1 : baseFlyoutZIndex)
  const nextDrawerZIndex = computed(() =>
    latestZIndex.value >= baseDrawerZIndex ? latestZIndex.value + 1 : baseDrawerZIndex
  )
  

  
  function isModalOpen (name) {
    return !!modals.value[name]
  }
  function isFlyoutOpen (name) {
    return !!flyouts.value[name]
  }
  function openModal ({ name, context, options = {} }) {
    // Currently, we only track one open modal at a time.
    if (isAnyModalOpen.value) {
      closeModal()
    }
    const zIndex = options.aboveIntercom
      ? Math.max(nextModalZIndex.value, zIndexAboveIntercom)
      : nextModalZIndex.value
    modals.value[name] = {
      name,
      context,
      zIndex
    }
    if (options.track) {
      trackOverlayInteraction(name, 'modal', 'open', context)
    }
  }
  function closeModal (name = null) {
    if (name && typeof name === 'string') {
      delete modals.value[name]
    } else {
      if (latestModal.value) {
        delete modals.value[latestModal.value.name]
      }
    }
  }
  function openFlyout ({ name, context, reuse = false, pushToStack = false }) {
    if (pushToStack && isAnyFlyoutOpen.value && latestFlyout.value) {
      const stackContext = { ...latestFlyout.value.context }
      
      if (latestFlyout.value.name === 'my-case') {
        const { myCaseFlyoutName, coreTabSelected } = storeToRefs(useCaseStore())
        stackContext.myCaseFlyoutName = myCaseFlyoutName.value
        stackContext.coreTabSelected = coreTabSelected.value
      }
      
      flyoutNavigationStack.value.push({
        name: latestFlyout.value.name,
        context: stackContext,
        zIndex: latestFlyout.value.zIndex
      })
    }
    
    // If the flyout is already open as the latest flyout, just update the context.
    if (reuse && flyoutName.value === name && !pushToStack) {
      flyouts.value[name].context = context
      return
    }
    
    // Currently, we only track one open flyout at a time.
    if (isAnyFlyoutOpen.value && !pushToStack) {
      closeFlyout()
    }
    flyouts.value[name] = {
      name,
      context,
      zIndex: nextFlyoutZIndex.value
    }
  }
  function closeFlyout (name = null) {
    const closedName = name && typeof name === 'string' ? name : latestFlyout.value?.name ?? null
    if (closedName) {
      lastClosedFlyoutName.value = closedName
      lastClosedFlyoutAt.value = Date.now()
    }
    if (name && typeof name === 'string') {
      delete flyouts.value[name]
    } else {
      if (latestFlyout.value) {
        delete flyouts.value[latestFlyout.value.name]
      }
    }
    flyoutNavigationStack.value = []
  }
  
  function goBackFlyout () {
    if (flyoutNavigationStack.value.length === 0) {
      return false
    }
    
    isNavigatingBack.value = true
    
    const previous = flyoutNavigationStack.value.pop()
    
    if (!previous) {
      isNavigatingBack.value = false
      return false
    }
    
    const currentFlyoutName = latestFlyout.value?.name
    
    if (previous.name === 'my-case' && previous.context.myCaseFlyoutName !== undefined) {
      const { setMyCaseFlyoutName, SET_CORE_TAB_SELECTED } = useCaseStore()
      setMyCaseFlyoutName(previous.context.myCaseFlyoutName)
      if (previous.context.coreTabSelected !== undefined) {
        SET_CORE_TAB_SELECTED(previous.context.coreTabSelected)
      }
    }
    
    flyouts.value[previous.name] = {
      name: previous.name,
      context: previous.context,
      zIndex: nextFlyoutZIndex.value
    }
    
    if (currentFlyoutName && currentFlyoutName !== previous.name) {
      delete flyouts.value[currentFlyoutName]
    }
    
    setTimeout(() => {
      isNavigatingBack.value = false
    }, 100)
    
    return true
  }
  
  function clearFlyoutNavigationStack () {
    flyoutNavigationStack.value = []
  }
  function openDrawer (name, options = {} ) {
    const { context, minimized = false } = options
    drawers.value[name] = {
      name,
      context: { ...context },
      minimized,
      zIndex: nextDrawerZIndex.value
    }
  }
  function closeDrawer (name = null) {
    if (name && typeof name === 'string') {
      delete drawers.value[name]
    } else if (latestDrawer.value) {
      delete drawers.value[latestDrawer.value.name]
    }
  }
  
  function minimizeDrawer (name) {
    if (name && typeof name === 'string') {
      drawers.value[name] = {
        name,
        context: { ...drawers.value[name].context },
        minimized: true,
        zIndex: nextDrawerZIndex.value
      }
    }
  }
  function closeLatest () {
    if (!latest.value) return false
    switch (latest.value.type) {
      case 'modal':
        closeModal(latest.value.name)
        break
      case 'flyout':
        closeFlyout(latest.value.name)
        break
      case 'drawer':
        closeDrawer(latest.value.name)
        break
      default:
        return false
    }
    return true
  }
  function closeAllModals () {
    modals.value = {}
  }
  function closeAllFlyouts () {
    flyouts.value = {}
  }
  function closeAllDrawers () {
    drawers.value = {}
  }
  function closeAll () {
    closeAllModals()
    closeAllFlyouts()
    closeAllDrawers()
  }

  return {
    modals,
    flyouts,
    drawers,
    drawerIsDragging,
    savedScrollPositions,
    flyoutNavigationStack,
    isNavigatingBack,
    lastClosedFlyoutName,
    lastClosedFlyoutAt,
    modalName,
    modalContext,
    modalZIndex,
    flyoutName,
    flyoutContext,
    flyoutZIndex,
    hasPreviousFlyout,
    previousFlyout,
    isAnyModalOpen,
    isAnyDrawerOpen,
    isAnyDrawerPresent,
    isAnyFlyoutOpen,
    isAnyOverlayOpen,
    latestModal,
    latestFlyout,
    latest,
    baseModalZIndex,
    baseFlyoutZIndex,
    latestZIndex,
    nextModalZIndex,
    nextFlyoutZIndex,
    nextDrawerZIndex,
    drawerName,
    drawerContext,
    drawerZIndex,
    drawerMinimized,
    isModalOpen,
    isFlyoutOpen,
    openModal,
    closeModal,
    openFlyout,
    closeFlyout,
    goBackFlyout,
    clearFlyoutNavigationStack,
    closeLatest,
    closeAllModals,
    closeAllFlyouts,
    closeAll,
    openDrawer,
    closeDrawer,
    minimizeDrawer,
    closeAllDrawers
  }
})

import.meta.hot?.accept(acceptHMRUpdate(useOverlaysStore, import.meta.hot))
